/**
 * Kick-triggered ducking ("sidechain"): every hit on a source drum lane dips a
 * target track's level and lets it recover, so an 808 or bass gets out of the
 * kick's way. Drum hits are scheduled events with known times, so the dip is
 * scheduled on the target's duck gain at each hit instead of measuring the
 * kick's audio. Live playback and offline renders therefore duck identically.
 *
 * A track opts in with `duck: { source, amountDb, releaseMs }`, where source
 * is a drum-lane track id. Pure module: shared with the chat music engine.
 */
export const DUCK_DEFAULTS = Object.freeze({ amountDb: -8, releaseMs: 180 });
export const DUCK_LIMITS = Object.freeze({ amountDb: [-24, 0], releaseMs: [30, 1000] });
// How fast the dip lands and how long it holds before recovering.
const ATTACK_SECONDS = 0.004;
const HOLD_SECONDS = 0.02;

const clamp = (value, [low, high]) => Math.min(high, Math.max(low, value));

/** A track's ducking settings, clamped, or null when it does not duck. */
export function normalizeDuck(duck) {
  if (!duck || typeof duck !== 'object' || typeof duck.source !== 'string' || !duck.source) return null;
  const amountDb = clamp(Number.isFinite(Number(duck.amountDb)) ? Number(duck.amountDb) : DUCK_DEFAULTS.amountDb, DUCK_LIMITS.amountDb);
  if (amountDb === 0) return null;
  const releaseMs = clamp(Number.isFinite(Number(duck.releaseMs)) ? Number(duck.releaseMs) : DUCK_DEFAULTS.releaseMs, DUCK_LIMITS.releaseMs);
  return { source: duck.source, amountDb, releaseMs };
}

/** The tracks a hit on this lane should duck. A lane never ducks itself. */
export function duckTargetsFor(tracks = [], sourceTrackId) {
  return (tracks || []).flatMap((track) => {
    const duck = track?.id !== sourceTrackId ? normalizeDuck(track?.duck) : null;
    return duck && duck.source === sourceTrackId ? [{ trackId: track.id, duck }] : [];
  });
}

/** Linear gain at the bottom of a full-velocity dip. */
export const duckFloor = (amountDb) => 10 ** (amountDb / 20);

/** Schedule one hit's dip and recovery on a gain AudioParam. Softer hits duck less. */
export function scheduleDuck(param, time, duck, velocity = 1) {
  if (!param || !Number.isFinite(time)) return;
  const strength = Math.min(1, Math.max(0, Number.isFinite(velocity) ? velocity : 1));
  const floor = duckFloor(duck.amountDb * strength);
  param.setTargetAtTime(floor, time, ATTACK_SECONDS / 3);
  // Four time constants is about 98% recovered by the end of the release.
  param.setTargetAtTime(1, time + ATTACK_SECONDS + HOLD_SECONDS, Math.max(0.005, duck.releaseMs / 1000 / 4));
}

/** Back to full level now, dropping every scheduled dip (stop, seek, re-route). */
export function resetDuck(param, now) {
  if (!param) return;
  param.cancelScheduledValues(0);
  param.setValueAtTime(1, now);
}
